'use client'
import { useState } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField } from "@/components/ui/form"
import {
    Field,
    FieldContent,
    FieldLabel,
    FieldError,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea"
import { Info, Plus } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { useAuth } from "@clerk/clerk-react"
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";


const addTopicSchema = z.object({
    name: z.string().min(1, "Topic name is required"),
    order: z.preprocess(
        (val) => val === "" ? undefined : parseFloat(val),
        z.number({ invalid_type_error: "Order must be a number" })
            .min(0.01, "Order is required")
    ),
    description: z.string().optional(),
})

export function AddTopicDialog({
    courseId,
    onTopicAdded,
    refetchTopics,
    triggerLabel = "Add Topic",
    triggerClassName = "",
}) {
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { getToken } = useAuth()

    const form = useForm({
        resolver: zodResolver(addTopicSchema),
        defaultValues: {
            name: "",
            order: "",
            description: "",
        },
    })

    const onSubmit = async (values) => {
        try {
            setIsSubmitting(true)
            const token = await getToken()
            if (!token) return;

            const payload = {
                ...values,
                order: parseFloat(Number(values.order).toFixed(1)), // Ensure order is a float with 1 decimal place
                course_id: courseId
            }

            console.log("Submitting payload:", payload)

            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            const response = await axios.post(api_endpoint + "/topic/create", payload, reqConfig)
            console.log("Topic created:", response.data.topic)
            toast.success(`Added topic "${values.name}" successfully!`)
            setOpen(false)
            form.reset() // Reset form after successful submission
            if (onTopicAdded) onTopicAdded(response.data.topic)
            if (refetchTopics) refetchTopics() // Refetch topics

        } catch (error) {
            console.error("Failed to create topic:", error)
            if (error.response?.status === 404) {
                toast.error("Topic creation endpoint not found. Please check the API endpoint.")
            } else if (error.response?.status === 401) {
                toast.error("Authentication failed. Please log in again.")
            } else if (error.response?.status === 400) {
                toast.error("Invalid data provided. Please check your input.")
            } else {
                toast.error("Failed to add topic. Please try again.")
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        form.reset() // Reset form when canceling
        setOpen(false)
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant=""
                    className={`${triggerClassName}`.trim()}
                >
                    {triggerLabel}
                    <Plus className="w-4 h-4" />
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg text-(--neutral-900) h-auto">
                <DialogHeader className="">
                    <DialogTitle className="font-heading">Add New Topic</DialogTitle>
                    <DialogDescription className="font-body">Create a new topic for your course.</DialogDescription>
                </DialogHeader>

                <div className="">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-topic-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Topic Title *</FieldLabel>
                                            <FieldContent>
                                                <Input
                                                    {...field}
                                                    placeholder="Enter topic title"
                                                    className={cn(
                                                        "mt-1",
                                                        fieldState.error &&
                                                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                    )}
                                                />
                                                <FieldError
                                                    errors={
                                                        fieldState.error
                                                            ? [{ message: "This is required" }]
                                                            : []
                                                    }
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="order"
                                    render={({ field, fieldState }) => (
                                        <Field>
                                            <FieldLabel>Order *</FieldLabel>
                                            <FieldContent>
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="any"
                                                    min="0.01"
                                                    pattern="^\d*\.\d+$"
                                                    placeholder="e.g. 1.0, 2.1, 3.2"
                                                    className={cn(
                                                        "mt-1",
                                                        fieldState.error &&
                                                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                    )}
                                                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : "")}
                                                />
                                                <FieldError
                                                    errors={
                                                        fieldState.error
                                                            ? [{ message: "This is required" }]
                                                            : []
                                                    }
                                                />
                                            </FieldContent>
                                        </Field>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field, fieldState }) => (
                                    <Field>
                                        <FieldLabel>
                                            Description
                                            <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                                <Info className="w-3 h-3" />
                                                Optional
                                            </div>
                                        </FieldLabel>
                                        <FieldContent>
                                            <Textarea
                                                {...field}
                                                rows={4}
                                                className={cn(
                                                    "min-h-[120px] resize-none mt-1",
                                                    fieldState.error &&
                                                        "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                                )}
                                                placeholder="Describe what this topic covers..."
                                            />
                                            <FieldError errors={[]} />
                                        </FieldContent>
                                    </Field>
                                )}
                            />
                        </form>
                    </Form>
                </div>

                <DialogFooter className="">
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className=""
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant=""
                        form="add-topic-form"
                        className=""
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center w-full">
                                <ClipLoader size={20} color="#fff" />
                            </span>
                        ) : (
                            "Add Topic"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}